import React, { useState, useMemo, useRef, useEffect } from "react";
import { useMarkets } from "../hooks/useMarkets";
import { useRates } from "../hooks/useRates";
import { formatPriceFlexible, formatPercentage, formatLargeNumber } from "../sdk/utils/display";
import { DEFAULT_BASE_URL } from "./smartlayout/constants";
import { useSubscription } from "../context/SubscriptionContext";
import { isMultiFeedPriceFeed } from "../sdk/types/api";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import "./smartlayout/layout.css";

interface TickerBarProps {
  baseURL: string;
  selectedMarketId: string;
  height: string;
  onMarketSelect?: (marketId: string) => void;
  favorites?: string[]; // Array of market IDs that are favorited
  onFavoriteToggle?: (marketId: string) => void;
}

interface TickerItem {
  marketId: string;
  symbol: string;
  price: number | null;
  priceChangePercent: number | null;
  isClosed: boolean;
  isFavorite: boolean;
}

type Timeframe = "1H" | "4H" | "1D" | "1W" | "1M";

function TickerBar({
  baseURL = DEFAULT_BASE_URL,
  selectedMarketId: propSelectedMarketId,
  height,
  onMarketSelect,
  favorites = [],
  onFavoriteToggle,
}: TickerBarProps): JSX.Element {
  const { markets, tickers, selectedMarketId, setSelectedMarketId, getSymbolByMarketId } =
    useMarkets({
      baseURL,
      autoFetchMarkets: true,
    });

  const { subscribeSubChannelWithRetention, unsubscribeSubChannel, isConnected, getRetainedData, client } = useSubscription();

  const [internalFavorites, setInternalFavorites] = useState<Set<string>>(
    new Set(favorites),
  );
  const [timeframe, setTimeframe] = useState<Timeframe>("1H");
  const [priceFeed, setPriceFeed] = useState<number | null>(null);
  const tickerScrollRef = useRef<HTMLDivElement>(null);
  const priceFeedCallbackRef = useRef<((data: any) => void) | null>(null);

  // Use prop or internal state for selected market
  const effectiveSelectedMarketId =
    propSelectedMarketId || selectedMarketId || null;

  // Use useRates hook for funding rate (and other rate data)
  const {
    latestFundingRate,
  } = useRates({
    subscriptionClient: client,
    baseURL,
    marketId: effectiveSelectedMarketId || "",
    historyDepth: 1, // Only need latest value
  });

  // Extract and convert funding rate from useRates
  const fundingRate = useMemo(() => {
    if (!latestFundingRate?.fundingRate) return null;
    try {
      return toNumberFromSatoshi(latestFundingRate.fundingRate);
    } catch (error) {
      console.error("[TickerBar] Error converting funding rate:", error);
      return null;
    }
  }, [latestFundingRate]);

  // Extract mark_price from funding rate as fallback for oracle price
  const markPriceFromRates = useMemo(() => {
    if (!latestFundingRate?.markPrice) return null;
    try {
      return toNumberFromSatoshi(latestFundingRate.markPrice);
    } catch (error) {
      console.error("[TickerBar] Error converting mark_price:", error);
      return null;
    }
  }, [latestFundingRate]);

  // Oracle price: prefer price_feed, fallback to mark_price from funding rate
  const oraclePrice = useMemo(() => {
    return priceFeed !== null ? priceFeed : markPriceFromRates;
  }, [priceFeed, markPriceFromRates]);

  // Merge external favorites with internal state
  const allFavorites = useMemo(() => {
    const merged = new Set(internalFavorites);
    favorites.forEach((id) => merged.add(id));
    return merged;
  }, [favorites, internalFavorites]);

  // Build ticker items from markets and tickers
  const tickerItems = useMemo((): TickerItem[] => {
    return markets
      .map((market) => {
        const marketId = market.index ?? market.marketIndex ?? "";
        const ticker = tickers.get(marketId);
        const symbol =
          market.moniker ||
          market.symbol ||
          (market.baseAsset && market.quoteAsset
            ? `${market.baseAsset}/${market.quoteAsset}`
            : "Unknown");

        // Determine if market is closed (no active price or market inactive)
        const isClosed =
          !market.active ||
          !ticker?.lastPrice ||
          (typeof ticker.lastPrice === "string" && ticker.lastPrice === "0");

        // Get price
        let price: number | null = null;
        if (ticker?.lastPrice) {
          if (typeof ticker.lastPrice === "string") {
            price = parseFloat(ticker.lastPrice);
          } else {
            price = ticker.lastPrice;
          }
        }

        // Get price change percent
        let priceChangePercent: number | null = null;
        if (ticker?.priceChangePercent24h) {
          if (typeof ticker.priceChangePercent24h === "string") {
            priceChangePercent = parseFloat(ticker.priceChangePercent24h);
          } else {
            priceChangePercent = ticker.priceChangePercent24h;
          }
        }

        return {
          marketId,
          symbol,
          price,
          priceChangePercent,
          isClosed,
          isFavorite: allFavorites.has(marketId),
        };
      })
      .filter((item) => item.marketId); // Filter out invalid items
  }, [markets, tickers, allFavorites]);

  // Get selected market details
  const selectedMarket = useMemo(() => {
    if (!effectiveSelectedMarketId) return null;
    return markets.find(
      (m) =>
        m.index === effectiveSelectedMarketId ||
        m.marketIndex === effectiveSelectedMarketId,
    );
  }, [markets, effectiveSelectedMarketId]);

  const selectedTicker = useMemo(() => {
    if (!effectiveSelectedMarketId) return null;
    return tickers.get(effectiveSelectedMarketId);
  }, [tickers, effectiveSelectedMarketId]);

  // Get selected market symbol
  const selectedSymbol = useMemo(() => {
    if (!selectedMarket) return "Select Market";
    return (
      selectedMarket.moniker ||
      selectedMarket.symbol ||
      (selectedMarket.baseAsset && selectedMarket.quoteAsset
        ? `${selectedMarket.baseAsset}/${selectedMarket.quoteAsset}`
        : "Unknown")
    );
  }, [selectedMarket]);

  // Get selected market description
  const selectedDescription = useMemo(() => {
    if (!selectedMarket) return "";
    const base = selectedMarket.baseAsset || "";
    const quote = selectedMarket.quoteAsset || "";
    if (base && quote) {
      return `${base} to ${quote}`;
    }
    return selectedMarket.moniker || selectedMarket.symbol || "";
  }, [selectedMarket]);

  // Get selected market price and change
  const selectedPrice = useMemo(() => {
    if (!selectedTicker?.lastPrice) return null;
    if (typeof selectedTicker.lastPrice === "string") {
      return parseFloat(selectedTicker.lastPrice);
    }
    return selectedTicker.lastPrice;
  }, [selectedTicker]);

  const selectedPriceChange = useMemo(() => {
    if (!selectedTicker?.priceChangePercent24h) return null;
    if (typeof selectedTicker.priceChangePercent24h === "string") {
      return parseFloat(selectedTicker.priceChangePercent24h);
    }
    return selectedTicker.priceChangePercent24h;
  }, [selectedTicker]);

  // Subscribe to price_feed for selected market
  useEffect(() => {
    if (!effectiveSelectedMarketId || !isConnected) {
      setPriceFeed(null);
      return;
    }

    const symbol = getSymbolByMarketId(effectiveSelectedMarketId);
    if (!symbol) {
      setPriceFeed(null);
      return;
    }

    // Check for retained data first from StreamMessage
    const retained = getRetainedData("price_feed");
    if (retained && isMultiFeedPriceFeed(retained) && retained.data?.price) {
      try {
        const price = toNumberFromSatoshi(retained.data.price);
        setPriceFeed(price);
      } catch (error) {
        console.error("[TickerBar] Error processing retained price_feed:", error);
      }
    }

    // Create callback for price_feed updates
    // The callback receives the data part directly (MultiFeedPriceFeedData)
    const handlePriceFeedUpdate = (data: any) => {
      try {
        // The callback receives the data part directly, which should have a price field
        if (data && typeof data === "object" && data.price) {
          const price = toNumberFromSatoshi(data.price);
          setPriceFeed(price);
        }
      } catch (error) {
        console.error("[TickerBar] Error processing price_feed update:", error);
      }
    };

    priceFeedCallbackRef.current = handlePriceFeedUpdate;

    // Subscribe to price_feed
    try {
      subscribeSubChannelWithRetention(
        effectiveSelectedMarketId,
        symbol,
        "price_feed",
        handlePriceFeedUpdate,
      );
    } catch (error) {
      console.error("[TickerBar] Failed to subscribe to price_feed:", error);
    }

    // Cleanup: unsubscribe when market changes or component unmounts
    return () => {
      if (priceFeedCallbackRef.current) {
        try {
          unsubscribeSubChannel("price_feed", priceFeedCallbackRef.current);
        } catch (error) {
          console.error("[TickerBar] Failed to unsubscribe from price_feed:", error);
        }
        priceFeedCallbackRef.current = null;
      }
    };
  }, [effectiveSelectedMarketId, isConnected, getSymbolByMarketId, subscribeSubChannelWithRetention, unsubscribeSubChannel, getRetainedData]);

  // Handle market selection
  const handleMarketSelect = (marketId: string) => {
    if (onMarketSelect) {
      onMarketSelect(marketId);
    } else {
      setSelectedMarketId(marketId);
    }
  };

  // Handle favorite toggle
  const handleFavoriteToggle = (marketId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newFavorites = new Set(allFavorites);
    if (newFavorites.has(marketId)) {
      newFavorites.delete(marketId);
    } else {
      newFavorites.add(marketId);
    }
    setInternalFavorites(newFavorites);
    if (onFavoriteToggle) {
      onFavoriteToggle(marketId);
    }
  };

  // Get asset icon (simple fallback - can be enhanced with actual icon mapping)
  const getAssetIcon = (symbol: string): string => {
    const baseAsset = symbol.split("/")[0]?.toUpperCase() || "";
    // Return a simple emoji or icon based on asset
    const iconMap: Record<string, string> = {
      BTC: "₿",
      ETH: "Ξ",
      EUR: "€",
      USD: "$",
      USDC: "$",
      USDT: "$",
    };
    return iconMap[baseAsset] || baseAsset.charAt(0) || "?";
  };


  return (
    <div
      className="ticker-bar-container"
      style={height ? { height } : undefined}
    >
      {/* Top Scrolling Ticker Bar */}
      <div className="ticker-bar-top" ref={tickerScrollRef}>
        <div className="ticker-bar-scroll">
          {tickerItems.map((item, index) => (
            <React.Fragment key={item.marketId}>
              <div
                className={`ticker-item ${
                  item.marketId === effectiveSelectedMarketId ? "active" : ""
                }`}
                onClick={() => handleMarketSelect(item.marketId)}
              >
                <span
                  className="ticker-favorite-star"
                  onClick={(e) => handleFavoriteToggle(item.marketId, e)}
                  style={{
                    cursor: "pointer",
                    opacity: item.isFavorite ? 1 : 0.3,
                  }}
                >
                  ★
                </span>
                <span className="ticker-asset-icon">
                  {getAssetIcon(item.symbol)}
                </span>
                <span className="ticker-symbol">{item.symbol}</span>
                <span className="ticker-value">
                  {item.isClosed ? (
                    <span className="ticker-closed">CLOSED</span>
                  ) : item.price !== null ? (
                    formatPriceFlexible(item.price)
                  ) : (
                    "--"
                  )}
                </span>
              </div>
              {index < tickerItems.length - 1 && (
                <div className="ticker-separator" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Bottom Detailed View */}
      <div className="ticker-bar-bottom">
        {/* Left: Asset Info */}
        <div className="ticker-detail-left">
          <div className="ticker-detail-icon-large">
            {selectedMarket ? getAssetIcon(selectedSymbol) : "?"}
          </div>
          <div className="ticker-detail-symbol-group">
            <div className="ticker-detail-symbol-row">
              <span className="ticker-detail-symbol">{selectedSymbol}</span>
            </div>
            <div className="ticker-detail-description">
              {selectedDescription}
            </div>
          </div>
          <div className="ticker-detail-price-group">
            <div className="ticker-detail-price">
              {selectedPrice !== null
                ? formatPriceFlexible(selectedPrice)
                : "--"}
            </div>
            {selectedPriceChange !== null && (
              <div
                className={`ticker-detail-change ${
                  selectedPriceChange >= 0 ? "positive" : "negative"
                }`}
              >
                {selectedPriceChange >= 0 ? "+" : ""}
                {formatPercentage(selectedPriceChange / 100)}
              </div>
            )}
          </div>
        </div>

        {/* Middle: Market Metrics */}
        <div className="ticker-detail-metrics">
          <div className="ticker-metric">
            <label className="ticker-metric-label" title="Oracle Price">
              Oracle Price
            </label>
            <span className="ticker-metric-value">
              {oraclePrice !== null
                ? formatPriceFlexible(oraclePrice)
                : "--"}
            </span>
          </div>
          <div className="ticker-metric">
            <label className="ticker-metric-label" title="Open Interest (Long)">
              Open Interest (L)
            </label>
            <span className="ticker-metric-value">
              {selectedTicker?.volume
                ? (() => {
                    const volumeNum = typeof selectedTicker.volume === "string" 
                      ? parseFloat(selectedTicker.volume) 
                      : selectedTicker.volume;
                    return `${formatLargeNumber(volumeNum)} / 20.0M USDC`;
                  })()
                : "--"}
            </span>
          </div>
          <div className="ticker-metric">
            <label
              className="ticker-metric-label"
              title="Open Interest (Short)"
            >
              Open Interest (S)
            </label>
            <span className="ticker-metric-value">
              {selectedTicker?.volume
                ? (() => {
                    const volumeNum = typeof selectedTicker.volume === "string" 
                      ? parseFloat(selectedTicker.volume) 
                      : selectedTicker.volume;
                    return `${formatLargeNumber(volumeNum)} / 20.0M USDC`;
                  })()
                : "--"}
            </span>
          </div>
          <div className="ticker-metric">
            <label className="ticker-metric-label" title="Skew">
              Skew
            </label>
            <span className="ticker-metric-value">50%/50%</span>
          </div>
          <div className="ticker-metric">
            <label className="ticker-metric-label" title="Funding Rate">
              Funding Rate
            </label>
            <span className={`ticker-metric-value ${
              fundingRate !== null && fundingRate < 0 ? "negative" : fundingRate !== null && fundingRate > 0 ? "positive" : ""
            }`}>
              {fundingRate !== null
                ? formatPercentage(fundingRate)
                : "--"}
            </span>
          </div>
        </div>

        {/* Right: Holdings & Timeframe */}
        <div className="ticker-detail-right">
          <div className="ticker-metric">
            <label className="ticker-metric-label" title="Holding (Long)">
              Holding (L)
            </label>
            <span className="ticker-metric-value positive">0.0012%</span>
          </div>
          <div className="ticker-metric">
            <label className="ticker-metric-label" title="Holding (Short)">
              Holding (S)
            </label>
            <span className="ticker-metric-value negative">0.0021%</span>
          </div>
        </div>
        <div className="ticker-timeframe-selector">
          <select
            className="ticker-timeframe-select"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value as Timeframe)}
          >
            <option value="1H">1H</option>
            <option value="4H">4H</option>
            <option value="1D">1D</option>
            <option value="1W">1W</option>
            <option value="1M">1M</option>
          </select>
        </div>
      </div>
    </div>
  );
}

export default React.memo(TickerBar);
